import { DragAndDropFileUploader } from "@app/shared";
import { FlyoutContext } from "@app/shared/Flyouts";
import { QESTextInput, QESButton } from "@app/shared/qes-standards";
import TutorialsService from "@app/tutorials/tutorialsService";
import { FC, useContext, useRef, useState } from "react";
import { toast } from "react-toastify";

interface EditTutorialsProps {
    containerReference: string;
    containerItem: string;
    itemExtension: string;
    fileExtensionSplitter: (filename: string) => [string, string];
    rowTitle: string;
    updateContainerUI: (newDataString: string) => void;
}

export const EditTutorialsFlyout: FC<EditTutorialsProps> =
    ({ containerReference, containerItem, itemExtension, fileExtensionSplitter, rowTitle, updateContainerUI }: EditTutorialsProps) => {
        const [newRowTitle, setNewRowTitle] = useState(rowTitle);
        const flyoutContext = useContext(FlyoutContext);
        const supportService = useRef(new TutorialsService()).current;


        const rename = (containerReference: string, containerItem: string, itemExtension: string, newRowTitle: string) => {
            const encodedTitle = encodeURIComponent(newRowTitle);
            return supportService.updateBlobSas(containerReference, containerItem, itemExtension, encodedTitle)
                .then((response) => {
                    updateContainerUI(response);
                });
        }

        return (
            <>
                <div className="form-group row">
                    <div className="col-6">
                        <QESTextInput
                            label="New Display Title:"
                            testId="tutorials__file-name-input"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setNewRowTitle(e.target.value) }}
                            value={newRowTitle}
                        />
                    </div>
                    <QESButton
                        className="align-self-end"
                        disabled={newRowTitle === ""}
                        qesTestId="edit-tutorials_rename"
                        onClick={
                            () => rename(containerReference, containerItem, itemExtension, newRowTitle).then(() => {
                                toast(<span>Tutorial &quot;{newRowTitle}&quot; successfully renamed</span>, {
                                    containerId: "z-index-9999",
                                    type: "success",
                                });
                                // the first idea was to _not_ close on rename, in case the user wanted to do more editing
                                // but there's a weird interaction where if the rename changed the list order of the tutorials,
                                // the flyout would suddenly be for a different tutorial. it's based on list index, not id or anything,
                                // and the list is sorted by most recently updated
                                flyoutContext.handleCloseFlyout();
                            })
                        }
                    >
                        Rename
                    </QESButton>
                </div>
                <p style={{ marginTop: "2rem" }}>Select a new file to upload. This will also update the display title. <span style={{ float: "right" }}>Current file is <i className="font-italic" style={{ marginLeft: ".10rem" }}>{containerItem}.{itemExtension}</i></span></p>
                <DragAndDropFileUploader
                    apiKeyName="tutorialFile"
                    displayName={newRowTitle}
                    disabled={newRowTitle === ""}
                    onUpload={async (data: FormData) => {
                        return new Promise<void>(async (resolve, reject) => {
                            try {
                                const info = data.get("tutorialFile");
                                // FormDataEntryValue is File | string, but we're banking on it being a File
                                if (info instanceof File) {
                                    const [basename, extension] = fileExtensionSplitter(info.name);
                                    if (info.size <= 2000000000) {
                                        const newDataStr = await supportService.deleteBlobSas(containerReference, containerItem, itemExtension)
                                            .then(async () => {
                                                rowTitle = (newRowTitle != "") ? newRowTitle : rowTitle;
                                                return supportService.updateBlobSas(containerReference, basename, extension, rowTitle, data);
                                            });
                                        updateContainerUI(newDataStr);
                                        toast(<span>Tutorial &quot;{newRowTitle}&quot; successfully updated</span>, {
                                            containerId: "z-index-9999",
                                            type: "success",
                                        });
                                        flyoutContext.handleCloseFlyout();
                                    }
                                    else {
                                        alert("File is too large to upload. File can be added directly to blob storage.");
                                    }
                                    resolve();
                                }
                            } catch {
                                reject();
                            }
                        });
                    }}
                    testId="tutorials__file-input"
                />
            </>
        );
  }